import { useState } from 'react'
import { useBillers, useCreateBiller, useUpdateBiller } from '../../api/queries'
import type { Biller } from '../../api/types'
import { useAuthenticatedSession } from '../../auth/session'
import { useT, type Translate } from '../../i18n'
import type { BusinessTime } from '../../time/businessTime'
import { Button, DataTable, EmptyState, Skeleton, StatusBadge, useToast, type Column } from '../../ui'
import { BillerFormModal, type BillerFormValues } from './BillerFormModal'
import { BillerNote } from './BillerNote'
import { truncateGatewayNames } from './gatewayNames'
import screen from '../screen.module.css'
import { apiErrorText } from '../../bff/errorText'

/** Sorted client-side: the whole platform is tens of organisations, not
 *  thousands, so a round trip per sort would be pure latency. */
const byName = (a: Biller, b: Biller): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

function buildColumns(
  t: Translate,
  businessTime: BusinessTime,
  actions: {
    readonly onEdit: (b: Biller) => void
    readonly onToggle: (b: Biller) => void
    readonly busyCode: string | null
  },
): ReadonlyArray<Column<Biller>> {
  return [
    { key: 'name', header: t('billers.name'), render: (b) => b.name },
    { key: 'code', header: t('billers.code'), render: (b) => <span className={screen.mono}>{b.biller_id}</span> },
    {
      key: 'kind',
      header: t('billers.kind'),
      render: (b) => (
        <StatusBadge tone={b.kind === 'beneficiary' ? 'info' : 'neutral'}>{t(`billers.kind.${b.kind}`)}</StatusBadge>
      ),
    },
    {
      key: 'status',
      header: t('billers.status'),
      render: (b) => (
        <StatusBadge tone={b.is_active ? 'success' : 'neutral'}>
          {b.is_active ? t('billers.active') : t('billers.inactive')}
        </StatusBadge>
      ),
    },
    {
      key: 'users',
      header: t('billers.users'),
      numeric: true,
      render: (b) => String(b.user_count),
    },
    {
      key: 'gateways',
      header: t('billers.gateways'),
      // The agreed display form comes from the API, so this screen and the
      // gateway screen cannot disagree about what a gateway is called. Two
      // names then "and N more", because a biller can hold enough of the
      // platform's ~200 gateways to break the row otherwise.
      // A beneficiary organisation holds slices, not gateways: the count is
      // what fits a cell, and it is the way into the edit form's Slices
      // section — the one place an operator assigns them.
      render: (b) =>
        b.kind === 'beneficiary'
          ? (
            <Button size="sm" variant="secondary" onClick={() => actions.onEdit(b)}>
              {t('billers.slicesHeld', { count: b.slice_count })}
            </Button>
          )
          : b.gateways.length === 0
            ? '—'
            : truncateGatewayNames(b.gateways.map((g) => g.gateway_name), t),
    },
    { key: 'created', header: t('billers.created'), render: (b) => businessTime.date(b.created_at) },
    {
      key: 'actions',
      header: t('users.actions'),
      render: (b) => {
        const busy = actions.busyCode === b.biller_id
        return (
          <div className={screen.actions}>
            <Button size="sm" variant="secondary" onClick={() => actions.onEdit(b)} disabled={busy}>
              {t('billers.edit')}
            </Button>
            <Button
              size="sm"
              variant={b.is_active ? 'danger' : 'secondary'}
              onClick={() => actions.onToggle(b)}
              loading={busy}
            >
              {b.is_active ? t('billers.deactivate') : t('billers.activate')}
            </Button>
          </div>
        )
      },
    },
  ]
}

/**
 * Platform administration: who the billers are, and which gateways each one
 * can see.
 *
 * Reached only by internal users — the nav entry is hidden for everyone else
 * and the API refuses them, so this screen does not re-check the role. It does
 * assume it: a biller user who typed the URL sees an error, which is correct
 * and is the API's answer rather than a second, divergent client-side rule.
 */
export function Billers() {
  const t = useT()
  const { businessTime } = useAuthenticatedSession()
  const { notify } = useToast()

  const billers = useBillers(true)
  const create = useCreateBiller()
  const update = useUpdateBiller()

  const [editing, setEditing] = useState<Biller | null>(null)
  const [creating, setCreating] = useState(false)
  const [busyCode, setBusyCode] = useState<string | null>(null)

  const fail = (error: unknown) =>
    notify('danger', apiErrorText(t, error).text)

  const submit = (values: BillerFormValues) => {
    if (editing) {
      // `gateways` is omitted on purpose: the API reads an absent field as
      // "don't touch", so a rename cannot clobber assignments being made on
      // the Gateways screen at the same time.
      // The cut-off travels only when it changed: an absent field is "don't
      // touch", same as gateways.
      const cutOffChanged = values.settlement_day_start !== (editing.settlement_day_start ?? '')
      update.mutate(
        {
          code: editing.biller_id,
          changes: { name: values.name, ...(cutOffChanged ? { settlement_day_start: values.settlement_day_start } : {}) },
        },
        {
          onSuccess: () => {
            notify('success', t('billers.updated.toast', { name: values.name }))
            setEditing(null)
          },
          onError: fail,
        },
      )
      return
    }
    create.mutate(values, {
      onSuccess: (result) => {
        // A partial success — the biller exists but its administrator could
        // not be invited — is shown as a warning, not a success. Reporting it
        // as "created and invited" would leave someone waiting for an e-mail
        // that will never arrive.
        if (result.warning) notify('info', result.warning)
        else notify('success', t('billers.created.toast', { name: values.name, email: values.first_user_email }))
        setCreating(false)
      },
      onError: fail,
    })
  }

  const toggle = (biller: Biller) => {
    const next = !biller.is_active
    if (!next && !window.confirm(t('billers.deactivate.confirm', { name: biller.name }))) return
    setBusyCode(biller.biller_id)
    update.mutate(
      { code: biller.biller_id, changes: { is_active: next } },
      {
        onSuccess: () =>
          notify(
            next ? 'success' : 'info',
            t(next ? 'billers.activated.toast' : 'billers.deactivated.toast', { name: biller.name }),
          ),
        onError: fail,
        onSettled: () => setBusyCode(null),
      },
    )
  }

  const rows = (billers.data ?? []).toSorted(byName)

  return (
    <section>
      <div className={screen.header}>
        <div>
          <h1 className={screen.title}>{t('billers.title')}</h1>
          <p className={screen.subtitle}>{t('billers.subtitle')}</p>
        </div>
        <Button onClick={() => setCreating(true)}>{t('billers.add')}</Button>
      </div>

      <BillerNote />

      {billers.isPending ? (
        <Skeleton variant="row" count={6} label={t('common.loading')} />
      ) : billers.error ? (
        <EmptyState
          glyph="⚠"
          title={t('error.loadFailed')}
          body={apiErrorText(t, billers.error).text}
          actions={<Button onClick={() => void billers.refetch()}>{t('error.retry')}</Button>}
        />
      ) : rows.length === 0 ? (
        <EmptyState glyph="⬢" title={t('billers.empty')} body={t('billers.emptyBody')} />
      ) : (
        <DataTable
          caption={t('billers.title')}
          columns={buildColumns(t, businessTime, { onEdit: setEditing, onToggle: toggle, busyCode })}
          rows={rows}
          rowKey={(b) => b.biller_id}
          stale={billers.isFetching}
          sortLabel={(column) => column}
        />
      )}

      <BillerFormModal
        open={creating || editing !== null}
        {...(editing ? { biller: editing } : {})}
        busy={create.isPending || update.isPending}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        onSubmit={submit}
      />
    </section>
  )
}
